import { calculateMultipleLoansInterestDeduction } from "../../localization/se";
import { MarketSE } from "../../localization";
import { getBestInterestDataByFixationPeriodId } from "../../interest";
import { monthlyCostDefaultLocalizationParameters } from "./default-values";
import { calculateLoanToValueRatio } from "../../loan";
const MONTHS_IN_YEAR = 12;
/**
 * Calculates main mortgage interest expenses on given interest rates, additional loans not included
 *
 * @param mortgageAmount Yearly interest expense before deduction from main mortgage
 * @param interestRates List interest, discount, interest with discount and effective interest as rates
 * @param adults Number of adults in household
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Monthly discounted interest expense with and without deduction, list interest expense and discount amount from main mortgage
 */
export function calculateFirstMonthInterestExpenses(mortgageAmount, interestRates, adults, localizationParameters) {
    const firstMonthListInterestExpense = Math.round((mortgageAmount * interestRates.listInterest) / MONTHS_IN_YEAR);
    const firstMonthlyLtvDiscountSavings = Math.round((mortgageAmount * interestRates.loanToValueDiscount) / MONTHS_IN_YEAR);
    const firstMonthlyMembershipDiscountSavings = Math.round((mortgageAmount * interestRates.membershipDiscount) / MONTHS_IN_YEAR);
    const yearlyInterest = mortgageAmount * interestRates.interestWithDiscount;
    const firstMonthInterestWithoutDeduction = interestRates.listInterest > 0
        ? firstMonthListInterestExpense - firstMonthlyLtvDiscountSavings - firstMonthlyMembershipDiscountSavings
        : Math.round(yearlyInterest / MONTHS_IN_YEAR);
    const yearlyDeduction = calculateMultipleLoansInterestDeduction({ yearMainMortgageInterest: yearlyInterest, yearAdditionalMortgageInterest: 0, yearPrivateLoanInterest: 0 }, adults, localizationParameters);
    const yearlyInterestWithDeduction = yearlyInterest - yearlyDeduction.mainMortgageInterestDeduction;
    const firstMonthInterestWithDeduction = Math.round(yearlyInterestWithDeduction / 12);
    return {
        listInterest: firstMonthListInterestExpense,
        loanToValueDiscountSavings: firstMonthlyLtvDiscountSavings,
        membershipDiscountSavings: firstMonthlyMembershipDiscountSavings,
        interestWithoutDeduction: firstMonthInterestWithoutDeduction,
        interestWithDeduction: firstMonthInterestWithDeduction,
    };
}
/**
 * Calculates mortgage amortization, interest and total mortgage expenses in one month for swedish market,
 * and an interest comparison, from user existing mortgage interest, as rate and expense difference
 *
 * @param input Input parameters for Monthly Cost widget
 * @param adults Number of adults in household
 * @param localizationParameters Localization parameters for Monthly Cost widget
 * @returns Monthly amortization data, interest expenses and mortgage expenses, and an interest comparison
 */
function calculateMonthlyExpenses(input, adults, localizationParameters) {
    const { mortgageAmount, housingValue, interestData, userInterestRate } = input;
    const { monthlyAmortization, amortizationRate } = MarketSE.getMonthlyAmortizationWithLTVRequirement(housingValue, mortgageAmount, localizationParameters);
    const { listInterest, loanToValueDiscountSavings, membershipDiscountSavings, interestWithoutDeduction, interestWithDeduction } = calculateFirstMonthInterestExpenses(mortgageAmount, interestData, adults, localizationParameters);
    const totalExpenseWithDeduction = monthlyAmortization + interestWithDeduction;
    const totalExpenseWithoutDeduction = monthlyAmortization + interestWithoutDeduction;
    const InterestRateDiff = userInterestRate ? Math.round((userInterestRate - interestData.interestWithDiscount) * 1000) / 1000 : 0;
    const InterestAmountDiff = (mortgageAmount * InterestRateDiff) / MONTHS_IN_YEAR;
    return {
        amortization: {
            amount: monthlyAmortization,
            rate: amortizationRate,
        },
        interestExpenses: {
            listInterest,
            loanToValueDiscountSavings,
            membershipDiscountSavings,
            interest: interestWithoutDeduction,
        },
        totalExpenseWithDeduction,
        totalExpenseWithoutDeduction,
        userInterestDiff: {
            rate: InterestRateDiff,
            amount: InterestAmountDiff,
        },
    };
}
/**
 * @returns Swedish localization parameters for Monthly Cost widget
 */
function getDefaultLocalizationParameters() {
    return monthlyCostDefaultLocalizationParameters;
}
export { calculateLoanToValueRatio, calculateMonthlyExpenses, getBestInterestDataByFixationPeriodId, getDefaultLocalizationParameters };
